import { useNavigate, useSearchParams } from 'react-router-dom';
import { cpf as cpfValidator } from 'cpf-cnpj-validator';
import { cloneDeep } from 'lodash';
import { useMemo } from 'react';
import { useAppContext } from '@/context';
import { useErrorHandler, useRedirectToRegister, useToast } from '@/hooks';
import {
  ForbiddenError,
  NotFoundError,
  UnexpectedError,
  loadTruckerProfileByIdService,
  loadTruckerProfileService,
} from '@/services';
import { RISK_ONBOARDING_URL } from '@/constants';
import { useSetFormValue } from '@/utils';
import { FormTrucker } from '@/types';

export const useAutoLoadTruckerProfile = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const search = searchParams.get('search');
  const trucker = searchParams.get('motorista');
  const cellPhone = searchParams.get('telefone');
  const { dispatch, state } = useAppContext();
  const setFormValue = useSetFormValue(dispatch);
  const redirectToRegister = useRedirectToRegister();
  const generalErrorHandler = useErrorHandler();

  const isTruckerDocument = useMemo(() => Boolean(trucker) && cpfValidator.isValid(trucker) && Boolean(cellPhone), []);
  const isTruckerId = useMemo(() => Boolean(trucker) && !cpfValidator.isValid(trucker), [trucker]);
  const hasTrucker = useMemo(() => isTruckerDocument || isTruckerId, [isTruckerId, isTruckerDocument]);

  const handleLoadTruckerData = (): Promise<FormTrucker> => {
    if (isTruckerDocument) {
      return loadTruckerProfileService({ cpf: trucker, cellPhone });
    }

    return loadTruckerProfileByIdService({ truckerId: trucker });
  };

  const handle = () => {
    if (Boolean(search)) return;

    dispatch({ action: 'trucker_profile_auto_load_start' });
    dispatch({ action: 'risk_form_reset' });
    dispatch({ action: 'trucker_profile_update_data', payload: {} });

    if (!hasTrucker) {
      return redirectToRegister();
    }

    const MAX_ATTEMPTS = 2;

    const execute = (attempt = 1) => {
      const errorHandler = (error: Error) => {
        if ((error instanceof NotFoundError || error instanceof UnexpectedError) && attempt >= MAX_ATTEMPTS) {
          dispatch({ action: 'trucker_profile_auto_load_end', payload: false });
          toast.add({
            title: 'Não foi possível carregar os dados. Por favor, digite novamente o CPF.',
            type: 'neutral',
          });
        } else if (error instanceof ForbiddenError) {
          navigate(RISK_ONBOARDING_URL, { replace: true });
        } else if (attempt <= MAX_ATTEMPTS) {
          execute(attempt + 1);
        } else {
          generalErrorHandler(error);
        }
      };

      const onSuccess = (data: FormTrucker) => {
        if (data.isRegistered && !data.truckerIsActive) {
          redirectToRegister(data.personId);
          return;
        }

        setFormValue('trucker', { ...state.risk.data.trucker, ...cloneDeep(data) });
        dispatch({ action: 'trucker_profile_update_data', payload: cloneDeep(data) });
        dispatch({ action: 'trucker_profile_auto_load_end', payload: true });
      };

      handleLoadTruckerData().then(onSuccess).catch(errorHandler);
    };

    execute();
  };

  return { handle };
};
